import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import DestinationCard from './DestinationCard';
import DestinationCardTwo from './DestinationCardTwo';
import { useTourismData } from '../../context/TourismContext';
import { formatPrice } from '../../services/api';

function DestinationInner() {
    const [activeTab, setActiveTab] = useState('tab-grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const postsPerPage = 9;
    
    const location = useLocation();
    
    const { 
        destinations, 
        categories, 
        loading, 
        errors, 
        fetchDestinations, 
        fetchCategories 
    } = useTourismData();

    // Read URL parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const categoryParam = searchParams.get('category');
        
        if (categoryParam) {
            setSelectedCategory(categoryParam);
        } else {
            setSelectedCategory('');
        }
        
        fetchCategories();
    }, [location.search, fetchCategories]);
    
    // Handle category filtering when categories are loaded and we have a selected category
    useEffect(() => {
        if (categories.results && categories.results.length > 0) {
            if (selectedCategory) {
                // Find the category by slug to get its ID
                const category = categories.results.find(cat => cat.slug === selectedCategory);
                if (category) {
                    fetchDestinations({ category: category.id });
                } else {
                    // Category not found, load all destinations
                    fetchDestinations();
                }
            } else {
                // No category selected, load all destinations
                fetchDestinations();
            }
        }
    }, [categories.results, selectedCategory, fetchDestinations]);

    // Filter and sort destinations
    const filteredDestinations = destinations.results || [];
    const totalPages = Math.ceil(filteredDestinations.length / postsPerPage);
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredDestinations.slice(indexOfFirstPost, indexOfLastPost);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchDestinations({ search: searchQuery });
        setCurrentPage(1);
    };

    const handleSort = (value) => {
        setSortBy(value);
        let ordering = '';
        switch (value) {
            case 'popularity':
                ordering = '-featured,-rating';
                break;
            case 'rating':
                ordering = '-rating';
                break;
            case 'date':
                ordering = '-created_at';
                break;
            case 'price':
                ordering = 'price';
                break;
            case 'price-desc':
                ordering = '-price';
                break;
            default:
                ordering = '-featured,-created_at';
        }
        fetchDestinations({ ordering });
        setCurrentPage(1);
    };

    if (loading.destinations) {
        return (
            <section className="space">
                <div className="container">
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading destinations...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (errors.destinations) {
        return (
            <section className="space">
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        <h4>Error loading destinations</h4>
                        <p>{errors.destinations}</p>
                    </div>
                </div>
            </section>
        );
    }
    return (
        <section className="space">
            <div className="container">
                <div className="th-sort-bar">
                    <div className="row justify-content-between align-items-center">
                        <div className="col-md-4">
                            <div className="search-form-area">
                                <form className="search-form" onSubmit={handleSearch}>
                                    <input 
                                        type="text" 
                                        placeholder="Search destinations..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <button type="submit">
                                        <i className="fa-light fa-magnifying-glass" />
                                    </button>
                                </form>
                            </div>
                        </div>
                        <div className="col-md-auto">
                            <div className="sorting-filter-wrap">
                                <div className="nav" role="tablist">
                                    <Link
                                        to="#"
                                        id="tab-destination-grid"
                                        data-bs-toggle="tab"
                                        data-bs-target="#tab-grid"
                                        role="tab"
                                        aria-controls="tab-grid"
                                        aria-selected="true"
                                        className={`${activeTab === 'tab-grid' ? 'active' : ''}`}
                                        type="button"
                                        onClick={() => setActiveTab('tab-grid')}
                                    >
                                        <i className="fa-light fa-grid-2" />
                                    </Link>
                                    <Link
                                        to="#"
                                        id="tab-destination-list"
                                        data-bs-toggle="tab"
                                        data-bs-target="#tab-list"
                                        role="tab"
                                        aria-controls="tab-list"
                                        aria-selected="false"
                                        className={`${activeTab === 'tab-list' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('tab-list')}
                                    >
                                        <i className="fa-solid fa-list" />
                                    </Link>
                                </div>
                                <form className="woocommerce-ordering" method="get">
                                    <select
                                        name="orderby"
                                        className="orderby"
                                        aria-label="destination order"
                                        value={sortBy}
                                        onChange={(e) => handleSort(e.target.value)}
                                    >
                                        <option value="" >
                                            Default Sorting
                                        </option>
                                        <option value="popularity">Sort by popularity</option>
                                        <option value="rating">Sort by average rating</option>
                                        <option value="date">Sort by latest</option>
                                        <option value="price">Sort by price: low to high</option>
                                        <option value="price-desc">Sort by price: high to low</option>
                                    </select>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xxl-9 col-lg-8">
                        <div className="tab-content" id="nav-tabContent">
                            <div className={`tab-pane fade ${activeTab === 'tab-grid' ? 'show active' : ''}`} id="tab-grid" role="tabpanel"
                            >
                                <div className="row gy-30">
                                    {currentPosts.length > 0 ? currentPosts.map((destination) => (
                                        <div key={destination.id} className="col-xxl-4 col-xl-6">
                                            <DestinationCard
                                                destinationID={destination.id}
                                                destinationSlug={destination.slug}
                                                destinationImage={destination.main_image}
                                                destinationTitle={destination.name}
                                                destinationPrice={formatPrice(destination.price)}
                                                destinationCountry={destination.country}
                                                destinationCity={destination.city}
                                                destinationRating={destination.rating}
                                                destinationReviewCount={destination.review_count}
                                                destinationDuration={destination.duration_days}
                                                destinationDifficulty={destination.difficulty}
                                                destinationDescription={destination.short_description}
                                                destinationFeatured={destination.featured}
                                            />
                                        </div>
                                    )) : (
                                        <div className="col-12">
                                            <div className="text-center py-5">
                                                <h4>No destinations found</h4>
                                                <p>Try adjusting your search or filters.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'tab-list' ? 'show active' : ''}`} id="tab-list" role="tabpanel"
                            >
                                <div className="row gy-30">
                                    {currentPosts.length > 0 ? currentPosts.map((destination) => (
                                        <div key={destination.id} className="col-12">
                                            <DestinationCardTwo
                                                destinationID={destination.id}
                                                destinationSlug={destination.slug}
                                                destinationImage={destination.main_image}
                                                destinationTitle={destination.name}
                                                destinationPrice={formatPrice(destination.price)}
                                                destinationCountry={destination.country}
                                                destinationCity={destination.city}
                                                destinationRating={destination.rating}
                                                destinationReviewCount={destination.review_count}
                                                destinationDuration={destination.duration_days}
                                                destinationDifficulty={destination.difficulty}
                                                destinationDescription={destination.short_description}
                                                destinationFeatured={destination.featured}
                                            />
                                        </div>
                                    )) : (
                                        <div className="col-12">
                                            <div className="text-center py-5">
                                                <h4>No destinations found</h4>
                                                <p>Try adjusting your search or filters.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="th-pagination text-center mt-60 mb-0">
                            <ul>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li key={i}>
                                        <Link
                                            className={currentPage === i + 1 ? 'active' : ''}
                                            to="#"
                                            onClick={() => handlePageChange(i + 1)}
                                        >
                                            {i + 1}
                                        </Link>
                                    </li>
                                ))}
                                {currentPage < totalPages && (
                                    <li>
                                        <Link className="next-page" to="#" onClick={() => handlePageChange(currentPage + 1)}>
                                            Next <img src="/assets/img/icon/arrow-right4.svg" alt="" />
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                    <div className="col-xxl-3 col-lg-4">
                        <aside className="sidebar-area style2">
                            <div className="widget widget_categories  ">
                                <h3 className="widget_title">Categories</h3>
                                <ul>
                                    <li>
                                        <Link to="/destinations" className={selectedCategory === '' ? 'active' : ''}>
                                            <img src="/assets/img/theme-img/map.svg" alt="" />
                                            All Destinations
                                        </Link>
                                        <span>({destinations.count || 0})</span>
                                    </li>
                                    {categories.results && categories.results.map((category) => {
                                        const destinationCount = destinations.results?.filter(dest => dest.category.id === category.id).length || 0;
                                        return (
                                            <li key={category.id}>
                                                <Link 
                                                    to={`/categories/${category.slug}`}
                                                >
                                                    <img src="/assets/img/theme-img/map.svg" alt="" />
                                                    {category.name}
                                                </Link>
                                                <span>({destinationCount})</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div className="widget  ">
                                <h3 className="widget_title">Recent Posts</h3>
                                <div className="recent-post-wrap">
                                    <div className="recent-post">
                                        <div className="media-img">
                                            <Link to="/blog/1">
                                                <img
                                                    src="/assets/img/blog/recent-post-1-1.jpg"
                                                    alt="Blog"
                                                />
                                            </Link>
                                        </div>
                                        <div className="media-body">
                                            <h4 className="post-title">
                                                <Link className="text-inherit" to="/blog/1">
                                                    Exploring The Green Spaces Of the island maldives
                                                </Link>
                                            </h4>
                                            <div className="recent-post-meta">
                                                <Link to="/blog">
                                                    <i className="fa-regular fa-calendar" />
                                                    22/6/ 2025
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="recent-post">
                                        <div className="media-img">
                                            <Link to="/blog/1">
                                                <img
                                                    src="/assets/img/blog/recent-post-1-2.jpg"
                                                    alt="Blog"
                                                />
                                            </Link>
                                        </div>
                                        <div className="media-body">
                                            <h4 className="post-title">
                                                <Link className="text-inherit" to="/blog/1">
                                                    Harmony With Nature Of Belgium Tour and travle
                                                </Link>
                                            </h4>
                                            <div className="recent-post-meta">
                                                <Link to="/blog">
                                                    <i className="fa-regular fa-calendar" />
                                                    25/6/ 2025
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="recent-post">
                                        <div className="media-img">
                                            <Link to="/blog/1">
                                                <img
                                                    src="/assets/img/blog/recent-post-1-3.jpg"
                                                    alt="Blog"
                                                />
                                            </Link>
                                        </div>
                                        <div className="media-body">
                                            <h4 className="post-title">
                                                <Link className="text-inherit" to="/blog/1">
                                                    Exploring The Green Spaces Of Realar Residence
                                                </Link>
                                            </h4>
                                            <div className="recent-post-meta">
                                                <Link to="/blog">
                                                    <i className="fa-regular fa-calendar" />
                                                    27/6/ 2025
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="widget widget_tag_cloud  ">
                                <h3 className="widget_title">Popular Tags</h3>
                                <div className="tagcloud">
                                    <Link to="/blog">Tour</Link>
                                    <Link to="/blog">Adventure</Link>
                                    <Link to="/blog">Rent</Link>
                                    <Link to="/blog">Innovate</Link>
                                    <Link to="/blog">Hotel</Link>
                                    <Link to="/blog">Modern</Link>
                                    <Link to="/blog">Luxury</Link>
                                    <Link to="/blog">Travel</Link>
                                </div>
                            </div>
                            <div
                                className="widget widget_offer  "
                                data-bg-src="/assets/img/bg/widget_bg_1.jpg"
                                style={{ backgroundImage: "url(/assets/img/bg/widget_bg_1.jpg)" }}
                            >
                                <div className="offer-banner">
                                    <div className="offer">
                                        <h6 className="box-title">
                                            Need Help? We Are Here To Help You
                                        </h6>
                                        <div className="banner-logo">
                                            <img src="/assets/img/logo2.svg" alt="Tourm" />
                                        </div>
                                        <div className="offer">
                                            <h6 className="offer-title">You Get Online support</h6>
                                            <Link className="offter-num" to={+256214203215}>
                                                +256 214 203 215
                                            </Link>
                                        </div>
                                        <Link to="/contact" className="th-btn style2 th-icon">
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>

    )
}

export default DestinationInner
